import { useEffect, useState } from "react";

/**
 * Matches YouTube CDN URLs that are eligible for the post-fetch 120×90
 * soft-404 dimension check. The path-shape anchor restricts the match
 * to the mq/hq/sd/maxresdefault variants — the legitimate `default.jpg`
 * variant is also 120×90 and is excluded (see spec 2026-05-03 A3).
 */
const YT_SOFT_404_HOST_RE =
  /^https?:\/\/(?:i\.ytimg\.com|img\.youtube\.com)\/vi\/[^/]+\/(?:mq|hq|sd|maxres)default\.(?:jpg|webp)(?:\?.*)?$/i;

export interface ThumbnailProps {
  /** Primary image URL. May be `null`, `undefined`, an empty string, or a URL that fails to load. */
  src?: string | null;
  /**
   * Optional secondary fallback URL (typically `item.channel_artwork_url`).
   * Tried after `src` errors. If it also errors (or is absent), the inline
   * cover-art SVG is rendered.
   */
  fallbackSrc?: string | null;
  /** Image alt text. Defaults to empty (decorative). */
  alt?: string;
  /**
   * Tailwind / CSS classes applied to the rendered element. Should encode
   * dimensions and shape (e.g. `"h-[68px] w-[120px] rounded-md object-cover"`).
   * Same className applies to both the `<img>` (when rendering) and the SVG
   * fallback wrapper, so the slot dimensions stay stable across states.
   */
  className?: string;
  /** Optional `loading` attribute on the `<img>`. Defaults to `"lazy"`. */
  loading?: "lazy" | "eager";
}

/**
 * Thumbnail with L14 three-state image fallback:
 *   (a) `src` truthy AND not yet errored → render <img src={src}>
 *   (b) primary <img> fires onError AND `fallbackSrc` is truthy → render <img src={fallbackSrc}>
 *   (c) `src` empty/null OR both URLs errored → render inline cover-art SVG
 *
 * The SVG fallback is a self-contained waveform/play motif on a neutral
 * background. It fills the container set by `className`, so call sites
 * control aspect ratio without the component having to know it.
 */
export function Thumbnail({
  src,
  fallbackSrc,
  alt = "",
  className,
  loading = "lazy",
}: ThumbnailProps) {
  type Phase = "primary" | "fallback" | "svg";
  const initialPhase: Phase = src ? "primary" : fallbackSrc ? "fallback" : "svg";
  const [phase, setPhase] = useState<Phase>(initialPhase);
  useEffect(() => {
    setPhase(src ? "primary" : fallbackSrc ? "fallback" : "svg");
  }, [src, fallbackSrc]);

  if (phase === "svg") {
    return <ThumbnailSVG className={className} />;
  }

  const currentSrc = phase === "primary" ? src : fallbackSrc;

  return (
    <img
      src={currentSrc ?? undefined}
      alt={alt}
      loading={loading}
      className={className}
      onLoad={(e) => {
        const img = e.currentTarget;
        if (
          img.naturalWidth === 120 &&
          img.naturalHeight === 90 &&
          YT_SOFT_404_HOST_RE.test(img.currentSrc || img.src)
        ) {
          if (phase === "primary" && fallbackSrc) {
            setPhase("fallback");
          } else {
            setPhase("svg");
          }
        }
      }}
      onError={() => {
        if (phase === "primary" && fallbackSrc) {
          setPhase("fallback");
        } else {
          setPhase("svg");
        }
      }}
    />
  );
}

/**
 * Inline cover-art SVG. Visual matches the shipped /static/upload-placeholder.svg
 * (waveform bars + circular play badge) but is rendered inline so the fallback
 * is synchronous and aspect-ratio-agnostic.
 */
function ThumbnailSVG({ className }: { className?: string }) {
  return (
    <div
      className={`${className ?? ""} flex items-center justify-center bg-[#1a1a2e]`}
      aria-hidden
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        className="h-[60%] w-[60%]"
        preserveAspectRatio="xMidYMid meet"
      >
        <rect x="88"  y="224" width="28" height="64"  rx="6" fill="#5a82f5" opacity="0.7" />
        <rect x="132" y="192" width="28" height="128" rx="6" fill="#5a82f5" />
        <rect x="176" y="240" width="28" height="32"  rx="6" fill="#5a82f5" opacity="0.8" />
        <rect x="220" y="160" width="28" height="192" rx="6" fill="#6c9fff" />
        <rect x="264" y="208" width="28" height="96"  rx="6" fill="#5a82f5" />
        <rect x="308" y="176" width="28" height="160" rx="6" fill="#5a82f5" />
        <rect x="352" y="224" width="28" height="64"  rx="6" fill="#5a82f5" opacity="0.7" />
      </svg>
    </div>
  );
}
